import { useState } from "react";
import { Service } from "@/types/dashboard";
import { useIsMobile } from "@/hooks/use-mobile";
import { ServiceDeleteDialog } from "./ServiceDeleteDialog";
import { ServicePriceDisplay } from "./price-display/ServicePriceDisplay";
import { ServiceActionButtons } from "./action-buttons/ServiceActionButtons";
import { ServiceCategorySelector } from "./category-selector/ServiceCategorySelector";
import { ServiceItemImage } from "./service-image/ServiceItemImage";
import { createCategory } from "@/utils/categoryUtils";
import { supabase } from "@/integrations/supabase/client";

interface ServiceItemProps {
  service: Service;
  userCategories: any[];
  onUpdateServiceCategory: (serviceId: string, categoryId: string, newUserCategories: any[]) => void;
  onEditService: (service: Service) => void;
  onDeleteService: (serviceId: string) => void;
  onPreviewService?: (service: Service) => void;
  onToggleVisibility?: (service: Service) => void;
  isDeleting: boolean;
  currency?: string | null;
  serviceImages: any[];
  onUserCategoriesUpdate?: (newCategories: any[]) => void;
}

export const ServiceItem = ({
  service,
  userCategories,
  onUpdateServiceCategory,
  onEditService,
  onDeleteService,
  onPreviewService,
  onToggleVisibility,
  isDeleting,
  currency,
  serviceImages,
  onUserCategoriesUpdate
}: ServiceItemProps) => {
  const isMobile = useIsMobile();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    await onDeleteService(service.id);
    setDeleteDialogOpen(false);
  };

  const handleHideInstead = async () => {
    if (onToggleVisibility && service.is_active) {
      await onToggleVisibility(service);
    }
    setDeleteDialogOpen(false);
  };

  const hasTravelFee = service.service_mode === 'in_person' && service.travel_fee && service.travel_fee > 0;
  
  // Get the actual service price (with discount if available)
  const servicePrice = service.discounted_price && service.discounted_price > 0 
    ? service.discounted_price 
    : service.price;

  const handleUpdateCategory = (categoryId: string, newUserCategories: any[] | null = null) => {
    onUpdateServiceCategory(service.id, categoryId, newUserCategories || userCategories);
  };

  // Add logic for creating a new category
  const handleCreateCategory = async (name: string): Promise<string | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
  
    // Create the new category
    const { data: newCategory, error: createError } = await supabase
      .from('service_categories')
      .insert({ 
        name: name.trim(), 
        user_id: user.id, 
        sequence: userCategories.length,
        is_visible: true 
      })
      .select()
      .single();
  
    if (createError || !newCategory) {
      console.error("Failed to create category:", createError);
      return null;
    }
  
    // Add the new category to the list and update the service immediately
    const newUserCategories = [...userCategories, newCategory];
    
    // Update local categories immediately
    if (onUserCategoriesUpdate) {
      onUserCategoriesUpdate(newUserCategories);
    }
    
    // Update the service with the new category
    handleUpdateCategory(newCategory.id, newUserCategories);

    return newCategory.id;
  };

  return (
    <div className="p-4 flex flex-col sm:flex-row sm:items-center hover:bg-gray-50/80">
      <div className="flex items-start mb-3 sm:mb-0 sm:mr-4">
        <ServiceItemImage 
          imageUrl={service.image_url} 
          serviceName={service.name} 
          serviceId={service.id}
          serviceImages={serviceImages}
        />
      </div>
      
      <div className="flex-1 mb-3 sm:mb-0">
        <h3 className="font-medium text-gray-900">{service.name}</h3>
        
        <ServicePriceDisplay 
          servicePrice={servicePrice}
          originalPrice={service.price}
          travelFee={service.travel_fee}
          hasTravelFee={hasTravelFee}
          currency={currency}
        />
      </div>
      
      <div className={`flex ${isMobile ? 'flex-col w-full' : 'items-center'} gap-3`}>
        <ServiceCategorySelector
          categoryId={service.category_id}
          userCategories={userCategories}
          onUpdateCategory={(categoryId) => handleUpdateCategory(categoryId)}
          isMobile={isMobile}
          onCreateCategory={handleCreateCategory}
        />
        
        <div className={`flex ${isMobile ? 'w-full justify-between' : 'items-center gap-2'}`}>
          <ServiceActionButtons
            service={service}
            onEditService={onEditService}
            onToggleVisibility={onToggleVisibility}
            onDeleteClick={handleDeleteClick}
            isDeleting={isDeleting}
            isMobile={isMobile}
          />
        </div>
      </div>

      <ServiceDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDelete={handleDelete}
        onHideInstead={service.is_active ? handleHideInstead : undefined}
        isDeleting={isDeleting}
      />
    </div>
  );
};
